import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CognitoUserPool, CognitoUserAttribute } from "amazon-cognito-identity-js";
import DatePicker from "react-datepicker";
import PhoneInput from "react-phone-input-2";
import { AddressAutofill } from "@mapbox/search-js-react";
import { rememberSignup } from "../../utils/signupFlow";
import Button from "../ui/Button"

import "react-datepicker/dist/react-datepicker.css";
import "react-phone-input-2/lib/style.css";
import "../../styles/components/create_account_page/SignUpForm.css";

const mapboxStyles = `
/* Keep suggestions on top and clickable */
.mapbox-search-portal,
.mapbox-address-autofill-popover,
.mapbox-address-popover {
  z-index: 999999 !important;
  pointer-events: auto !important;
  touch-action: none !important;
}
`;

const SignUpForm = ({ userType }) => {
  const navigate = useNavigate();
  const submitting = useRef(false);
  const mounted = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Basic user info
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  // Address fields
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");

  // lat/lng captured from AddressAutofill or fallback geocode
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  // For phone dial code logic
  const [currentDialCode, setCurrentDialCode] = useState("");

  const passwordRequirements = [
    { label: "8+ characters", met: password.length >= 8 },
    { label: "Uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Lowercase letter", met: /[a-z]/.test(password) },
    { label: "Number", met: /\d/.test(password) },
    { label: "Special character", met: /[^A-Za-z0-9]/.test(password) },
  ];

  const passwordIsValid = passwordRequirements.every((requirement) => requirement.met);

  // On date change
  const handleBirthdateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      const isoDate = offsetDate.toISOString().split("T")[0];
      setBirthdate(isoDate);
    } else {
      setBirthdate("");
    }
  };

  // On phone change
  const handlePhoneChange = (value, data) => {
    setPhoneNumber(`+${value}`);
    setCurrentDialCode(data.dialCode);
  };

  // Called if the user selects an autocomplete suggestion from Mapbox
  const handleAddressRetrieve = (res) => {
    console.log("Mapbox onRetrieve =>", res);
    if (res.features && res.features[0]?.center?.length === 2) {
      setLng(res.features[0].center[0]);
      setLat(res.features[0].center[1]);
    }
  };

  // Validate required fields
  const validateRequiredFields = () => {
    if (!addressLine1 || !city || !zip || !state) {
      setErrorMessage("Please fill in Address Line 1, City, Country/State, and Post Code/Zip Code.");
      return false;
    }
    return true;
  };

  // Actual sign-up flow
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;

    setErrorMessage("");

    if (!passwordIsValid) {
      setErrorMessage("Please make sure your password meets all of the requirements above.");
      return;
    }

    if (!validateRequiredFields()) return;
    submitting.current = true;
    setIsSubmitting(true);

    // If lat/lng not set by user picking from suggestions, do a final geocode fetch.
    let finalLat = lat;
    let finalLng = lng;

    if (!finalLat || !finalLng) {
      const addressString = [addressLine1, addressLine2, city, state, zip]
        .filter(Boolean)
        .join(", ");

      const mapboxToken = "pk.eyJ1Ijoicmh5c2dpIiwiYSI6ImNtNXN2djRwcjBueWsya3NlZ2IzeHA4NGcifQ.XNbZeepgUkZ1NRo-lBBoaA";
      const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        addressString
      )}.json?limit=1&access_token=${mapboxToken}`;

      try {
        const resp = await fetch(geocodeUrl);
        const data = await resp.json();
        if (data.features && data.features.length > 0) {
          finalLng = data.features[0].center[0];
          finalLat = data.features[0].center[1];
        }
      } catch (err) {
        console.error("Error doing final geocode fetch:", err);
        // You could set an error message or fallback here if needed.
      }
    }

    // Now embed lat/long in the address JSON
    const addressObject = {
      address_line_1: addressLine1 || "",
      address_line_2: addressLine2 || "",
      city: city || "",
      state: state || "",
      zip: zip || "",
      lat: finalLat || "",
      lng: finalLng || "",
    };

    const finalAddressJson = JSON.stringify(addressObject);

    const poolData = {
      UserPoolId: "eu-west-2_nyRkWiHVC",
      ClientId: "fsp09nn4h6a084bod215if65q",
    };
    const userPool = new CognitoUserPool(poolData);

    const attributeList = [
      new CognitoUserAttribute({ Name: "email", Value: email.trim() }),
      new CognitoUserAttribute({ Name: "given_name", Value: givenName }),
      new CognitoUserAttribute({ Name: "family_name", Value: familyName }),
      new CognitoUserAttribute({ Name: "birthdate", Value: birthdate }),
      new CognitoUserAttribute({ Name: "phone_number", Value: phoneNumber }),
      new CognitoUserAttribute({ Name: "address", Value: finalAddressJson }),
      new CognitoUserAttribute({ Name: "custom:userRole", Value: userType }),
    ];

    // Perform the sign-up
    if (!mounted.current) return;
    userPool.signUp(email.trim(), password, attributeList, null, (err, result) => {
      if (!mounted.current) return;
      submitting.current = false;
      setIsSubmitting(false);
      if (err) {
        console.error("Error during sign-up:", err);
        setErrorMessage(err.message || "An error occurred during sign-up.");
        return;
      }


      // Navigate to next screen
      const signupId = rememberSignup(email.trim(), password);
      navigate("/verify-email", { state: {
        email: email.trim(), signupId, signupSub: result.userSub,
        verified: Boolean(result.userConfirmed),
      } });
    });
  };

  return (
    <>
      <style>{mapboxStyles}</style>

      <form className="signup-form" onSubmit={handleSubmit} autoComplete="on" aria-busy={isSubmitting}>
      <div className="signup-form__intro">
        <h1>Create your account</h1>
        <p>
          {userType === "photographer"
            ? "Join ViewFinders as a photographer."
            : "Create a client account to find and contact photographers."}
        </p>
      </div>

      <div className="signup-form__group">
        <div className="signup-form__group-heading">
          <h2>Account details</h2>
        </div>
        <div className="form-section form-account">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          required
        />

        <div className="password-field">
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              placeholder="Password"
              required
              minLength={8}
              aria-describedby="password-requirements"
            />
            <button
              type="button"
              className="password-visibility-toggle"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {(isPasswordFocused || (password && !passwordIsValid)) && (
          <div
            id="password-requirements"
            className="password-requirements"
            aria-label="Password requirements"
            aria-live="polite"
          >
            {passwordRequirements.map((requirement) => (
              <span
                key={requirement.label}
                className={`password-requirement ${requirement.met ? "is-met" : ""}`}
              >
                <span className="password-requirement__icon" aria-hidden="true">
                  {requirement.met ? "✓" : "•"}
                </span>
                {requirement.label}
              </span>
            ))}
          </div>
        )}
        </div>
      </div>

      <div className="signup-form__group">
        <div className="signup-form__group-heading">
          <h2>About you</h2>
        </div>
        <div className="form-section form-name">

        <input
          type="text"
          value={givenName}
          onChange={(e) => setGivenName(e.target.value)}
          placeholder="First Name"
          required
        />

        <input
          type="text"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          placeholder="Last Name"
          required
        />

        </div>

        <div className="form-section form-personal">

      <div className="date-input">
        <DatePicker
          selected={selectedDate}
          onChange={handleBirthdateChange}
          dateFormat="dd/MM/yyyy"
          placeholderText="Birthdate"
          maxDate={new Date()}
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={100}
        />
      </div>

      <div className="phone-input-wrapper">
        <PhoneInput
          country="gb"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder="Phone Number"
          enableSearch
          required={userType === "photographer"}
        />
      </div>

        </div>
      </div>

      <div className="signup-form__group">
        <div className="signup-form__group-heading">
          <h2>Location</h2>
          <p>Used for local discovery and photographer matching.</p>
        </div>
        <div className="form-section form-address">

      <AddressAutofill
        accessToken="pk.eyJ1Ijoicmh5c2dpIiwiYSI6ImNtNXN2djRwcjBueWsya3NlZ2IzeHA4NGcifQ.XNbZeepgUkZ1NRo-lBBoaA"
        onRetrieve={handleAddressRetrieve}
      >

      <input
        type="text"
        autoComplete="address-line1"
        placeholder="Address Line 1"
        value={addressLine1}
        onChange={(e) => setAddressLine1(e.target.value)}
        required
      />

      <input
        type="text"
        autoComplete="address-line2"
        placeholder="Address Line 2"
        value={addressLine2}
        onChange={(e) => setAddressLine2(e.target.value)}
      />

      <input
        type="text"
        autoComplete="address-level2"
        placeholder="City"
        value={city}
        onChange={(e) => setCity(e.target.value)}
        required
      />

      <input
        type="text"
        autoComplete="address-level1"
        placeholder="Country / State"
        value={state}
        onChange={(e) => setState(e.target.value)}
        required
      />

      <input
        type="text"
        autoComplete="postal-code"
        placeholder="Post Code / Zip Code"
        value={zip}
        onChange={(e) => setZip(e.target.value)}
        required
      />

      </AddressAutofill>

        </div>
      </div>

      <div className="form-submit">
        <Button type="submit" variant="primary" size="lg" className="signup-submit-button" disabled={isSubmitting}>
          {isSubmitting ? "Creating account…" : "Create account"}
        </Button>
      </div>

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      </form>
    </>
  );
};

export default SignUpForm;

